import React from "react";
import { subResourceIntegrity } from "../client/Graphql/hash"
declare const window: any
interface Module {
  moduleType: string;
  contentData?: {
    seasons: any[];
  }[];
}
function serializeJsonLd(schema: object): string {
  return JSON.stringify(schema).replace(/<\//g, '\\u003c/');
}

function trimContentItem(item: any) {
  if (!item) return item;
  const trimmed = { ...item };
  if (trimmed.seasons && Array.isArray(trimmed.seasons)) {
    trimmed.seasons = trimmed.seasons.slice(0, 1).map((s: any) => ({
      ...s,
      episodes: s.episodes ? s.episodes.slice(0, 1) : [],
    }));
  }
  if (trimmed.creditBlocks) trimmed.creditBlocks = [];
  if (trimmed.relatedVideos) trimmed.relatedVideos = [];
  if (trimmed.recommendedContent) trimmed.recommendedContent = [];
  if (trimmed.tags) trimmed.tags = [];
  return trimmed;
}

function trimPageDataForSSR(pageData: any) {
  if (!pageData?.page?.modules) return pageData;
  const trimmed = { ...pageData, page: { ...pageData.page } };
  trimmed.page.modules = trimmed.page.modules.map((mod: any, idx: number) => {
    if (idx === 0) return mod;
    if (!mod.contentData || !Array.isArray(mod.contentData)) return mod;
    const trimmedMod = { ...mod };
    trimmedMod.contentData = mod.contentData.slice(0, 3).map(trimContentItem);
    return trimmedMod;
  });
  return trimmed;
}

function trimSiteConfigForSSR(siteConfig: any) {
  if (!siteConfig) return siteConfig;
  const trimmed = JSON.parse(JSON.stringify(siteConfig));
  const main = trimmed?.appcmsMain;
  if (main) {
    if (main.monetizationPlans) {
      main.monetizationPlans = main.monetizationPlans.map((plan: any) => ({
        id: plan.id,
        identifier: plan.identifier,
        name: plan.name,
        type: plan.type,
        planDetails: plan.planDetails,
      }));
    }
    if (main.languages && Array.isArray(main.languages)) {
      main.languages = main.languages.map((lang: any) => ({
        code: lang.code, name: lang.name, isDefault: lang.isDefault,
      }));
    }
    delete main.toolConfig;
    delete main.templateBuilder;
  }
  const platform = trimmed?.appcmsPlatform;
  if (platform) {
    if (platform.analytics?.convivaConfig?.metadata) {
      platform.analytics.convivaConfig.metadata = {};
    }
  }
  return trimmed;
}

function extractMetaTag(metaTagString: string | null) {
  if(metaTagString) {
    const nameMatch = metaTagString.match(/name="([^"]*)"/);
    const contentMatch = metaTagString.match(/content="([^"]*)"/);
    const name = nameMatch ? nameMatch[1] : '';
    const content = contentMatch ? contentMatch[1] : '';
    return { name, content };
  }
  return null;
}

function hasValidMonetizationModel(plans) {
  const validModels = ["SVOD", "TVE", "TVOD"];
  return plans?.some(plan => validModels.includes(plan.type));
}

function normalizeStructuredDataUrl(url: string) {
  let normalized = url.trim();
  normalized = normalized.replace(/^r\d+x\d+:/i, "");
  normalized = normalized.replace(/^:+(?=https?:\/\/)/i, "");
  return normalized;
}

function appendImageResizePolicy(src: string | null | undefined, w = 1920, h = 1080): string | null {
  if (!src || typeof src !== 'string') return null;
  const trimmed = src.trim();
  if (!trimmed || !/^https?:\/\//i.test(trimmed)) return null;
  if (trimmed.includes("impolicy")) {
    const srcSplit = trimmed.split("impolicy");
    return `${srcSplit[0]}impolicy=resize&w=${w}&h=${h}`;
  }
  return `${trimmed}?impolicy=resize&w=${w}&h=${h}`;
}

function resolveStructuredDataThumbnailUrl(fullPageData: any, siteConfig: any): string | null {
  const rawThumbnail =
    fullPageData?.metadataMap?.thumbnailUrl
    ?? fullPageData?.modules?.[0]?.contentData?.[0]?.gist?.imageGist?.r16x9
    ?? fullPageData?.modules?.[0]?.contentData?.[0]?.gist?.videoImageUrl?.r16x9
    ?? siteConfig?.appcmsMain?.images?.placeholderCover;

  let candidate: string | null = null;

  if (typeof rawThumbnail === "string") {
    candidate = rawThumbnail;
  } else if (rawThumbnail && typeof rawThumbnail === "object") {
    const map = rawThumbnail as Record<string, unknown>;
    const preferredKeys = ["r16x9", "r3x4", "r1x1", "r2x3"];
    for (const key of preferredKeys) {
      const value = map[key];
      if (typeof value === "string" && value) {
        candidate = value;
        break;
      }
    }
    if (!candidate) {
      const fallback = Object.values(map).find(
        (value) => typeof value === "string" && (value as string).trim()
      );
      candidate = typeof fallback === "string" ? fallback : null;
    }
  }

  if (!candidate) {
    return appendImageResizePolicy(siteConfig?.appcmsMain?.images?.placeholderCover);
  }

  const normalized = normalizeStructuredDataUrl(candidate);
  if (!/^https?:\/\//i.test(normalized)) {
    return appendImageResizePolicy(siteConfig?.appcmsMain?.images?.placeholderCover);
  }

  return appendImageResizePolicy(normalized);
}

 export default function Html({ siteConfig, pageData, browserDetails, children, title ,xApiKey, cachedAPI,apiBaseUrl,sseBaseUrl,analyticsBaseUrl,wssBaseUrl,env,ip,embedCodeToken,toolsUrl,telemetryIngestEndpoint,templateUrl,requestId, cssHref = undefined, scriptSrcs = undefined, isPreview = false}: {siteConfig: any, pageData: any, browserDetails: any, children: any, title: any, xApiKey: any, cachedAPI: any, apiBaseUrl: any, sseBaseUrl: any, analyticsBaseUrl: any, wssBaseUrl: any, env: any, ip: any, embedCodeToken: any, toolsUrl: any, telemetryIngestEndpoint?: string, templateUrl: any, requestId: any, cssHref?: string | null, scriptSrcs?: string[] | null, isPreview?: boolean}) {
  const fontUrl = siteConfig?.appcmsMain?.brand?.font?.fontUrl
  const embeddedStyles: string | null = siteConfig?.appcmsMain?.features?.embeddedStyles
  const styleContentMatch = embeddedStyles?.match(/<style\b[^>]*>([\s\S]*?)<\/style>/i);
  const outputStyles = styleContentMatch ? styleContentMatch[1] : null;
  const extractedMeta = extractMetaTag(embeddedStyles);
  const name = extractedMeta?.name;
  const content = extractedMeta?.content;
  const rawFontFamily = siteConfig?.appcmsMain?.brand?.font?.fontFamily;
  const fontFamily =
    rawFontFamily === "Roboto"
      ? "RobotoRegular"
      : (rawFontFamily || "AcuminProRegular");
  var jsonLDFlag = false;
  let paidContent = false;
  var convertSecondsToHms = (d) => {
    d = Number(d);
    var h = Math.floor(d / 3600);
    var m = Math.floor(d % 3600 / 60);
    var s = Math.floor(d % 3600 % 60);
    var hDisplay = h > 0 ? h + "H" : "";
    var mDisplay = m > 0 ? m + "M" : "";
    var sDisplay = s > 0 ? s + "S" : "";
    return 'PT' + hDisplay + mDisplay + sDisplay;
  }
  var fullPageData = pageData?.page || {}
  const firstModuleImage = fullPageData?.modules?.[0]?.contentData?.[0]?.gist?.imageGist?.r16x9 
    || fullPageData?.modules?.[0]?.contentData?.[0]?.gist?.videoImageUrl?.r16x9
    || fullPageData?.metadataMap?.thumbnailUrl?.r16x9;
  const lcpImageUrl = firstModuleImage ? `${firstModuleImage}?impolicy=resize&w=1920&h=1080` : null;
  const lcpImageBase = firstModuleImage || null;
  const lcpSrcSet = lcpImageBase
    ? `${lcpImageBase}?impolicy=resize&w=480&h=270 480w, ${lcpImageBase}?impolicy=resize&w=768&h=432 768w, ${lcpImageBase}?impolicy=resize&w=1920&h=1080 1920w`
    : null;
  let type = "website"
  if(fullPageData.title === "Video Page") {
    type = "video.movie"
  } else if(fullPageData.title === "Show Page") {
    type = "video.tv_show"
  } else if(fullPageData.title === "Article Page") {
    type = "article"
  } else if(fullPageData.title === "Event Page" || fullPageData.title === "Game Detail Page") {
    type = "event"
  }
  var uploadDate, videoUploadDate, seriesUploadDate, duration, pageTitle, description = "", showDetailModule: Module | undefined = undefined, seasons: any[] = [], mappedSeasons: any[] = [], videoDetailModule: Module | undefined = undefined
  pageTitle = pageData?.page?.metadataMap?.title
  if(pageTitle == null) {
    pageTitle = pageData?.page?.title || title
  }
  if(!(fullPageData?.metadataMap?.isLiveStream) && (fullPageData?.metadataMap?.contentType === 'VIDEO' || fullPageData?.metadataMap?.contentType === "SERIES")) {
    jsonLDFlag = true;
    var videoTitle = fullPageData?.metadataMap?.title;
    // Fall back to gist.title when metadataMap.title is absent, so the JSON-LD
    // "name" field is never empty (Google Search Console "Missing field name").
    if (!videoTitle) {
      if (fullPageData?.metadataMap?.contentType === "VIDEO") {
        const vdm = fullPageData?.modules?.find((m: any) => m.moduleType === "VideoDetailModule");
        videoTitle = vdm?.contentData?.[0]?.gist?.title;
      } else if (fullPageData?.metadataMap?.contentType === "SERIES") {
        const sdm = fullPageData?.modules?.find((m: any) => m.moduleType === "ShowDetailModule");
        videoTitle = sdm?.contentData?.[0]?.gist?.title;
      }
    }
    pageTitle = videoTitle ? videoTitle.replace(/["']/g, "") : (pageData?.page?.title || title || ""); //Replace Both Double and Single Quotes
    if(fullPageData?.metadataMap?.uploadDate < 1e10) {
      videoUploadDate = new Date(fullPageData?.metadataMap?.uploadDate * 1000);
    } else {
      videoUploadDate = new Date(fullPageData?.metadataMap?.uploadDate);
    }
    uploadDate = videoUploadDate.toISOString();
    seriesUploadDate = videoUploadDate.toISOString().slice(0, 10);
    duration = fullPageData?.metadataMap?.duration ? convertSecondsToHms(fullPageData?.metadataMap?.duration) : null
    if(fullPageData?.metadataMap?.contentType === "VIDEO") {
      videoDetailModule = fullPageData?.modules?.filter(module => module.moduleType === "VideoDetailModule");
      var videoDescription = videoDetailModule?.[0]?.contentData?.[0]?.gist?.description
        || fullPageData?.metadataMap?.description;
      description = videoDescription
        ? videoDescription?.replace(/["']/g, "").replace(/\n/g, " ")
        : (pageTitle || "").replace(/["']/g, "");
      paidContent = hasValidMonetizationModel(videoDetailModule?.[0]?.contentData?.[0]?.plans)
    }
    if(fullPageData?.metadataMap?.contentType === "SERIES") {
      showDetailModule = fullPageData?.modules?.filter(module => module.moduleType === "ShowDetailModule");
      var seriesDescription = showDetailModule?.[0]?.contentData?.[0]?.gist?.description
        || fullPageData?.metadataMap?.description;
      description = seriesDescription
        ? seriesDescription?.replace(/["']/g, "").replace(/\n/g, " ")
        : (pageTitle || "").replace(/["']/g, "").replace(/\n/g, " ");
      seasons = showDetailModule?.[0]?.contentData?.[0]?.seasons ?? [];
      mappedSeasons = seasons.map((season, index) => {
        const seasonData: any = {
          "@type": "TVSeason",
          name: season.title,
          numberOfEpisodes: season.episodes.length.toString(),
        };
        seasonData.episode = season.episodes.map((episode, epIndex) => ({
          "@type": "TVEpisode",
          episodeNumber: (epIndex + 1).toString(),
          name: episode.title,
          duration: convertSecondsToHms(episode.runtime)
        }));
      return seasonData;
      });
    }
  }

  const structuredDataThumbnailUrl = jsonLDFlag
    ? resolveStructuredDataThumbnailUrl(fullPageData, siteConfig)
    : null;

  let jsonLDInput
  if(jsonLDFlag) {
    if(fullPageData?.metadataMap?.contentType === 'VIDEO') {
      const videoHostBase = `https://${browserDetails.host}`;
      const videoEmbedUrl = `${videoHostBase}${pageData?.path || ''}`;
      const videoSchema: any = {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        "name": pageTitle,
        "description": description,
        "uploadDate": uploadDate,
        "duration": duration,
        "isAccessibleForFree": !paidContent,
        "embedUrl": videoEmbedUrl,
      };
      if (structuredDataThumbnailUrl) {
        videoSchema.thumbnailUrl = structuredDataThumbnailUrl;
      }
      jsonLDInput = serializeJsonLd(videoSchema)
    } else if(fullPageData?.metadataMap?.contentType === "SERIES"){
      const seriesContentPath = pageData?.page?.metadataMap?.contentUrl;
      const seriesHostBase = `https://${browserDetails.host}`;
      const seriesUrl = seriesContentPath ? `${seriesHostBase}${seriesContentPath}` : `${seriesHostBase}${pageData?.path || ''}`;
      const seriesSchema: any = {
        "@context": "https://schema.org",
        "@type": "TVSeries",
        "name": pageTitle,
        "containsSeason": mappedSeasons ? mappedSeasons : [],
        "description": description,
        "datePublished": seriesUploadDate,
        "url": seriesUrl,
      };
      if (structuredDataThumbnailUrl) {
        seriesSchema.thumbnailUrl = structuredDataThumbnailUrl;
      }
      jsonLDInput = serializeJsonLd(seriesSchema)
    }
  }
      const trimmedPageData = isPreview ? pageData : trimPageDataForSSR(pageData);
      const trimmedSiteConfig = trimSiteConfigForSSR(siteConfig);
      let appData = `window.isPreview=${JSON.stringify(Boolean(isPreview))}; window.apiBaseUrl=${JSON.stringify(apiBaseUrl)}; window.sseBaseUrl=${JSON.stringify(sseBaseUrl)}; window.xApiKey=${JSON.stringify(xApiKey)};
        window.cachedAPI=${JSON.stringify(cachedAPI)}; window.app_data = ${JSON.stringify(trimmedSiteConfig)}; window.page_data = ${JSON.stringify(trimmedPageData)}; 
        window.browserDetails = ${JSON.stringify(browserDetails)}; window.analyticsBaseUrl=${JSON.stringify(analyticsBaseUrl)}; 
        window.wssBaseUrl=${JSON.stringify(wssBaseUrl)}; window.env=${JSON.stringify(env)}; window.ip=${JSON.stringify(ip)}; window.embedCodeToken=${JSON.stringify(embedCodeToken)}; 
        window.toolsUrl=${JSON.stringify(toolsUrl)}; window.TELEMETRY_INGEST_ENDPOINT=${JSON.stringify(telemetryIngestEndpoint || '')}; window.templateUrl=${JSON.stringify(templateUrl)};window.requestId=${JSON.stringify(requestId)};window.cssHref=${JSON.stringify(cssHref || null)};window.scriptSrcs=${JSON.stringify(scriptSrcs || null)}`
      return (
      <html lang="en" style={{"margin": 0}}>
        <head>
          <meta charSet="utf-8" />
          <meta httpEquiv="x-dns-prefetch-control" content="on"></meta>
          <title>{pageTitle}</title>
          {name && content && <meta name={name} content={content} />}
          <meta property="og:url" content={`${('https://'+browserDetails?.host + browserDetails?.path) || browserDetails?.host || ""}`}></meta>
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <meta name="description" content={`${(pageData?.page?.metadataMap?.description) || `${description}` || ""}`} />
          <meta name="keywords" content={`${(pageData?.page?.metadataMap?.keywords) || ""}`} />
          <meta property="og:type" content={`${(pageData?.page?.metadataMap?.contentType) ? (pageData?.page?.metadataMap?.contentType).toLowerCase() : "website"}`} />
          <meta property="og:title" content={`${pageTitle || ""}`} />
          <meta property="og:description" content={`${(pageData?.page?.metadataMap?.description) || `${description}` || ""}`} />
          <meta property="og:site_name" content={`${(siteConfig?.appcmsMain?.site) || ""}`} />
          <meta name="mobile-web-app-capable" content="yes"/>
          <meta name="apple-mobile-web-app-status-bar-style" content="black"/>
          <meta name="apple-mobile-web-app-title" content={`${(siteConfig?.appcmsMain?.site) || ""}`}/>
          <meta property="og:image" content={`${(pageData?.page?.metadataMap?.thumbnailUrl?.r16x9) || siteConfig?.appcmsMain?.images?.placeholderCover}?impolicy=resize&w=1920&h=1080`} />
          <link rel="shortcut icon" type="image/x-icon" href={`${(siteConfig?.appcmsPlatform?.images?.favicon?.imageUri) || ""}`} />
          <link rel="canonical" href={`${('https://'+browserDetails?.host + browserDetails?.path)}`}></link>
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:site" content={`@${(siteConfig?.appcmsMain?.site) || ""}`}/>
          <meta name="twitter:creator" content={`@${(siteConfig?.appcmsMain?.site) || ""}`}/>
          <meta property="twitter:title" content={`${pageTitle || ""}`}></meta>
          <meta property="twitter:description" content={`${(pageData?.page?.metadataMap?.description) || `${description}` || ""}`}></meta>
          <meta property="twitter:image" content={`${(pageData?.page?.metadataMap?.thumbnailUrl?.r16x9) || siteConfig?.appcmsMain?.images?.placeholderCover}?impolicy=resize&w=1920&h=1080`} />
          {lcpSrcSet ? (
            <link rel="preload" as="image" imageSrcSet={lcpSrcSet} imageSizes="100vw" fetchPriority="high" />
          ) : lcpImageUrl ? (
            <link rel="preload" href={lcpImageUrl} as="image" fetchPriority="high" />
          ) : null}
          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
          {/* `precedence` opts these stylesheets into React 19's stylesheet
              registry: React dedupes by href, orders by precedence, and makes
              Suspense boundaries wait for them to load before unblocking. */}
          {fontUrl && (
            <link
              href={`${fontUrl}&display=swap`}
              rel="stylesheet"
              precedence="high"
            />
          )}
          {/* GTM preconnect removed: GTM loads lazily via initGTM(), preconnect wastes a connection */}
          {/* Do NOT add `precedence` here — React 19 would gate Suspense
              boundaries on this stylesheet's load state and cause a blank
              flash during client-side route transitions when the chunk is
              already loaded but not yet in React's resource registry. */}
          {cssHref && <link rel="stylesheet" href={cssHref} />}
          {outputStyles && (
            <style
              href="embedded-site-styles"
              precedence="high"
              dangerouslySetInnerHTML={{ __html: outputStyles }}
            />
          )}
        </head>
        <body className="site-background-color site-color" style={{
            "margin": 0,
            "overflowX": "hidden",
            "textRendering": "auto",
            "fontSize": "1em",
            "fontFamily": `${fontFamily} !important`
          }}>
          <noscript
            dangerouslySetInnerHTML={{
              __html: `<b>Enable JavaScript to run this app.</b>`
            }}
          />
          {children}
          <script
            dangerouslySetInnerHTML={{
              __html: appData
            }}
            integrity = {subResourceIntegrity(appData)}
          />
          {jsonLDFlag ? (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: jsonLDInput
             }}
             integrity = {subResourceIntegrity(jsonLDInput)}
           ></script>
         ) : ''}
         {scriptSrcs && scriptSrcs.map((scriptSrc, i) => (
           <script key={i} src={scriptSrc} defer={true} />
         ))}
         </body>
         </html>
    );
  }